// Keyboard-wedge badge scanner FSM — DESIGN.md section 9.4.
//
// kmb-scanner re-types every physical scan through one virtual keyboard as a fast keystroke
// burst ending in Enter, prefixed `<scannerId>=`. We listen globally for that shape: a run of
// printable keys terminated by Enter, with a minimum length (so a human hitting Enter alone
// never triggers a "scan") and a staleness guard (so a burst that never reaches Enter is
// eventually abandoned rather than accumulating forever). While a text input has focus we stand
// down entirely — the player-search box needs real typing.
//
// 2026-08-31 (round-3 live bug fix) — REWRITTEN to mirror app-vue2's v2 hub scanner pages
// (HeadlessHubDispatchView.vue, LogisticsPickingView.vue): a `keypress` listener on `document`,
// not a `keydown` listener on `window`. Root cause of "scans do nothing on the real kiosk" even
// though keystrokes provably reached the document: the OLD `keydown`-based FSM reset its buffer
// on ANY keydown whose `e.key.length !== 1` — which fires for bare MODIFIER keydowns (Shift,
// Control, CapsLock…), not just real shortcuts. A physical keyboard-wedge scan of a JWT-ish
// token (badge JWTs / idshare_ tokens / vCard lines) is loaded with uppercase letters and
// symbols that need Shift on a US keymap (`_`, `:`, `-`, letters) — Chromium fires a SEPARATE
// `Shift` keydown (key:'Shift', length 6) immediately before each such character, wiping
// whatever had been buffered so far. In practice this meant the buffer almost never survived to
// Enter with 8+ chars, so `lengthOk` failed and the scan was silently dropped — exactly the
// symptom reported. `keypress` doesn't have this problem: browsers never fire a keypress for a
// bare modifier key, only for the actual character it produces (already shifted, e.g. 'B'), so
// there is nothing to reset the buffer mid-scan. This is also why app-vue2's HID scanner views
// (and the canonical `useHidBarcodeScanner` composable they're slated to migrate to) all use
// `document` + `keypress` with no per-key timing gate at all.
//
// Lifted in spirit from app-v3's layer-1 lib/barcodeScanner.ts; this is its own copy — no
// cross-app import (this project is fully standalone, see DESIGN.md section 1).
import { identityResolve } from './backend'
import type { IdentityResolveResponse } from './types'

export interface ScannerOptions {
  minLength?: number
  maxDurationMs?: number
}

const DEFAULTS: Required<ScannerOptions> = {
  minLength: 8,
  // Staleness guard (the "timing guard" this FSM keeps, unlike the v2 hub pages it mirrors —
  // those are used in short-lived dialogs; this one is live for the app's entire lifetime, so an
  // abandoned burst that never reaches Enter should eventually self-clear).
  maxDurationMs: 8000,
}

// Strip an optional `<scannerId>=` prefix: any run of 1-40 non-'=' chars followed by '='.
const PREFIX_RE = /^[^=]{1,40}=/

// A whole signed vCard badge (docs/kombo-identity.md section 3) arrives as MANY
// Enter-terminated burst lines — the wedge types the QR's CRLFs as Enter. On a
// line ending in BEGIN:VCARD we switch to accumulation until END:VCARD, then
// resolve the joined text as ONE scan. Folded continuation lines start with a
// space and are plain burst lines here; the server unfolds them.
const VCARD_TOTAL_TIMEOUT_MS = 20000

export function stripScannerPrefix(raw: string): string {
  return raw.replace(PREFIX_RE, '')
}

function isTextInputFocused(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}

export interface ScannerHandle {
  stop: () => void
}

/**
 * Attaches the global burst listener. On a valid burst, strips the scanner prefix and resolves
 * it via POST /api/identity/resolve, invoking onResolved with the result. onError is invoked for
 * scan bursts that fail to resolve (unknown badge, backend unreachable, etc).
 */
export function initScanner(
  onResolved: (result: IdentityResolveResponse, rawScan: string) => void,
  onError?: (err: unknown, rawScan: string) => void,
  options: ScannerOptions = {},
): ScannerHandle {
  const opts = { ...DEFAULTS, ...options }

  let buffer = ''
  let burstStart = 0
  let vcardLines: string[] | null = null
  let vcardStart = 0

  function reset() {
    buffer = ''
    burstStart = 0
  }

  function resetVcard() {
    vcardLines = null
    vcardStart = 0
  }

  function handleKeypress(e: KeyboardEvent) {
    if (isTextInputFocused()) {
      reset()
      resetVcard()
      return
    }

    const now = performance.now()

    // Staleness guards — a burst/vCard that never reached its terminator is abandoned rather
    // than accumulating (or being committed) forever.
    if (burstStart > 0 && now - burstStart > opts.maxDurationMs) reset()
    if (vcardStart > 0 && now - vcardStart > VCARD_TOTAL_TIMEOUT_MS) resetVcard()

    if (e.key === 'Escape') {
      reset()
      resetVcard()
      return
    }
    if (e.key === 'Backspace') {
      buffer = buffer.slice(0, -1)
      return
    }

    if (e.key === 'Enter') {
      // Inside a vCard: every Enter-terminated line belongs to it (no minLength — "END:VCARD"
      // and short folded tails are valid lines).
      if (vcardLines) {
        e.preventDefault()
        vcardLines.push(buffer)
        const line = buffer
        reset()
        if (line === 'END:VCARD') {
          const rawScan = vcardLines.join('\n')
          resetVcard()
          identityResolve(rawScan).then(
            (result) => onResolved(result, rawScan),
            (err) => onError?.(err, rawScan),
          )
        }
        return
      }

      const line = stripScannerPrefix(buffer)
      if (line.length < opts.minLength) {
        // Too short to be a real scan (a stray Enter) — let it pass through untouched.
        reset()
        return
      }

      e.preventDefault()
      reset()
      if (line === 'BEGIN:VCARD') {
        // First line of a signed vCard badge — accumulate until END:VCARD.
        vcardLines = [line]
        vcardStart = now
        return
      }
      identityResolve(line).then(
        (result) => onResolved(result, line),
        (err) => onError?.(err, line),
      )
      return
    }

    // Only single printable characters accumulate — `keypress` doesn't fire for bare modifier
    // keys (Shift/Control/Alt/CapsLock/arrows/…), so this is effectively always true for real
    // scanner/keyboard input; the guard just ignores whatever oddity might still slip through.
    if (e.key.length !== 1) return

    if (buffer.length === 0) burstStart = now
    buffer += e.key
  }

  document.addEventListener('keypress', handleKeypress)

  return {
    stop() {
      document.removeEventListener('keypress', handleKeypress)
      reset()
      resetVcard()
    },
  }
}
